/**
 * PulseFeed — the real My Pulse feed.
 *
 * Reads `GET /api/pulse/feed/user/{handle}` which unifies canonical
 * `pulse_posts` with legacy sources so an existing user's profile
 * never launches empty.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { Loader2, Sparkles, Heart, MessageCircle, Share2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DEST_LABELS = {
  socialpulse: "SocialPulse",
  nature:      "NaturePulse",
  farmers:     "FarmersPulse",
  food:        "FoodPulse",
  sellnbuy:    "SellnBuy",
  photo:       "PhotoPulse",
  traveler:    "TravelerPulse",
  alumni:      "AlumniPulse",
};
const DEST_COLORS = {
  socialpulse: "#7C3AED", nature: "#10B981", farmers: "#F59E0B",
  food: "#EF4444", sellnbuy: "#1F6FEB", photo: "#EC4899",
  traveler: "#0EA5E9", alumni: "#8B5CF6",
};


export default function PulseFeed({ handle, isOwner, refreshToken }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/pulse/feed/user/${handle}`, { withCredentials: true })
      .then((r) => setItems(r.data.items || []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, [handle, refreshToken]);

  if (loading) {
    return (
      <section className="rounded-xl bg-white border p-8 text-center opacity-70"
               style={{ borderColor: "#E5E7EB" }}>
        <Loader2 className="w-4 h-4 animate-spin inline mr-2" /> Loading pulse…
      </section>
    );
  }

  if (items.length === 0) {
    return (
      <section data-testid="pulse-feed-empty"
               className="rounded-xl bg-white border p-8 text-center"
               style={{ borderColor: "#E5E7EB" }}>
        <div className="inline-flex items-center justify-center w-12 h-12 rounded-full mb-3"
             style={{ background: "rgba(124,58,237,0.10)", color: "#7C3AED" }}>
          <Sparkles className="w-6 h-6" />
        </div>
        <h3 className="font-display text-lg mb-1" style={{ color: "#0F172A" }}>My Pulse</h3>
        <p className="text-[13.5px] max-w-[420px] mx-auto" style={{ color: "#64748B" }}>
          {isOwner
            ? "Post an update, share a photo, or start writing — every post you cross-publish to a sister app will appear here."
            : `@${handle} hasn't published anything to their Pulse yet.`}
        </p>
      </section>
    );
  }

  return (
    <section data-testid="pulse-feed" className="space-y-3">
      <div className="flex items-center justify-between px-1">
        <h3 className="font-semibold text-[15px]" style={{ color: "#0F172A" }}>My Pulse</h3>
        <div className="text-[11.5px] opacity-60">{items.length} posts</div>
      </div>
      {items.map((it) => <PulseCard key={`${it.source}-${it.id}`} item={it} />)}
    </section>
  );
}


function PulseCard({ item }) {
  const dt = item.created_at ? new Date(item.created_at) : null;
  const dtLabel = dt
    ? dt.toLocaleDateString("en", { month: "short", day: "numeric", year: "numeric" })
    : "";
  return (
    <article
      data-testid={`pulse-card-${item.id}`}
      className="rounded-xl bg-white border p-4"
      style={{ borderColor: "#E5E7EB" }}
    >
      <header className="flex items-center gap-3 mb-3">
        <img
          src={item.author?.avatar_url
                || `https://api.dicebear.com/7.x/initials/svg?seed=${item.author?.handle}`}
          alt="" className="w-9 h-9 rounded-full object-cover"
        />
        <div className="flex-1 min-w-0">
          <div className="text-[13.5px] font-semibold truncate" style={{ color: "#0F172A" }}>
            {item.author?.name}
          </div>
          <div className="text-[11.5px] opacity-70">{dtLabel}</div>
        </div>
        {(item.placements || []).length > 0 && (
          <div className="flex flex-wrap gap-1 justify-end max-w-[180px]">
            {item.placements.slice(0, 3).map((p) => (
              <span key={p}
                    className="text-[10px] font-medium px-1.5 py-0.5 rounded-full"
                    style={{
                      background: `${DEST_COLORS[p] || "#7C3AED"}18`,
                      color:      DEST_COLORS[p] || "#7C3AED",
                    }}>
                #{DEST_LABELS[p] || p}
              </span>
            ))}
          </div>
        )}
      </header>

      {item.text && (
        <p className="text-[14.5px] leading-[1.55] whitespace-pre-wrap mb-3"
           style={{ color: "#0F172A" }}>
          {item.text}
        </p>
      )}

      {item.hero_image && (
        <img
          src={typeof item.hero_image === "string"
                ? item.hero_image
                : item.hero_image.url}
          alt="" loading="lazy"
          className="w-full max-h-[420px] object-cover rounded-lg mb-3"
        />
      )}

      <footer className="flex items-center gap-4 pt-2 text-[12.5px] border-t"
              style={{ borderColor: "#F1F5F9", color: "#64748B" }}>
        <button className="inline-flex items-center gap-1.5 hover:text-slate-900">
          <Heart className="w-3.5 h-3.5" />
          {Object.values(item.reactions || {}).reduce((a, b) => a + (b || 0), 0) || ""}
          <span className="opacity-70">Like</span>
        </button>
        <button className="inline-flex items-center gap-1.5 hover:text-slate-900">
          <MessageCircle className="w-3.5 h-3.5" />
          {item.comments || ""} <span className="opacity-70">Comment</span>
        </button>
        <button className="inline-flex items-center gap-1.5 hover:text-slate-900">
          <Share2 className="w-3.5 h-3.5" /> <span className="opacity-70">Share</span>
        </button>
      </footer>
    </article>
  );
}
